/**
 * The credentials this project is administered with.
 *
 * The project record already carries five free-text link columns - the site
 * admin, the hosting panel, the database, cPanel and the template - and four of
 * them are the address half of something that also has a username and a
 * password. Those two halves were living in different places: the address on the
 * project, where anybody with `manage:crm.projects` could read it, and the
 * password either in the vault under a title nobody connected to this project or
 * in nowhere at all. This tab is the other half arriving next to the first.
 *
 * ## It is a view of the vault, not a second vault
 *
 * Every row here is a `VaultEntry` whose `projectId` is this project. Nothing on
 * this screen owns a credential; the register at `/vault` does, and this is the
 * same rows read with one filter. That is why the row is `VaultRow` itself
 * rather than a project-shaped copy of it: a credential must not look like a
 * different kind of record depending on which page found it, and a copy would
 * drift the first time either list gained a column.
 *
 * The client column comes along even though every entry on a project ought to
 * belong to that project's client. It is not repetition, it is the check: an
 * entry filed under the wrong client is exactly the sort of thing that is
 * invisible in the register (where every row has a different client) and obvious
 * here (where one row disagrees with the rest).
 *
 * ## Two permissions, and the tab is absent without the first
 *
 * Reading the vault is `read:crm.vault`; writing it is `manage:crm.vault`.
 * Neither follows from `manage:crm.projects`, so `ProjectDetail` does not draw
 * the tab at all without the read permission, and the controls below are absent
 * rather than disabled without the write one. A button that renders and then
 * 403s reads as a broken feature instead of one this operator does not have.
 *
 * ## No password is ever on this page
 *
 * Same rule as the register: the list response carries "a password is on file"
 * and nothing more. Reading one is a press with a dialog, a countdown and an
 * audit row behind it (`RevealDialog`), and the plaintext lives in that
 * component's state until it closes.
 */
import { useCallback, useEffect, useState } from "react";
import { KeyRound, Loader2, Plus, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform } from "../../../lib/auth";
import {
  deleteVaultEntry,
  getVaultEntries,
  getVaultStatus,
  type VaultEntry,
  type VaultStatus,
} from "../../../lib/vault";
import { useCrmUser } from "../../shell/CrmUser";
import { Btn, Card, CardHead, EmptyState, Modal, listCard, useToast } from "../../ui";
import { COLS, VaultRow } from "../vault/VaultRow";
import { VaultModal } from "../vault/VaultModal";
import { RevealDialog } from "../vault/RevealDialog";

/**
 * The list route's own ceiling, and this tab does not page.
 *
 * A project with more than a hundred logins is a data problem rather than a
 * paging problem. The footer says so when the total is bigger than what is
 * drawn, instead of the list quietly ending at row one hundred - a truncation
 * nobody is told about is indistinguishable from the whole answer.
 */
const MAX_ROWS = 100;

export function ProjectVault({
  projectId,
  clientId,
  onChange,
}: {
  projectId: string;
  /** The project's client, so a credential added here is filed under both
   *  rather than under the project alone. */
  clientId: string;
  /** Tells the page frame that the count on this tab's pill is now stale. */
  onChange?: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const canManage = userCanPlatform(useCrmUser(), "manage:crm.vault");

  // Null while the first read is in flight, so the spinner and the empty card
  // are two states rather than one.
  const [rows, setRows] = useState<VaultEntry[] | null>(null);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<VaultStatus | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<VaultEntry | null>(null);
  const [revealing, setRevealing] = useState<VaultEntry | null>(null);
  const [toDelete, setToDelete] = useState<VaultEntry | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // A failed status read leaves `status` null, which draws no banner and
    // disables nothing: the server is the one that refuses a write it cannot
    // encrypt, and guessing "the key is missing" from a network error would
    // take the add button away on a vault that is working perfectly.
    getVaultStatus()
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await getVaultEntries({ projectId, pageSize: MAX_ROWS });
      setRows(res.rows);
      setTotal(res.total);
    } catch {
      // Said out loud, because the empty card cannot say it: "no credentials on
      // this project" is exactly what a failed read would otherwise draw, and on
      // a tab where zero is the ordinary answer nobody would ever doubt it.
      setRows([]);
      setTotal(0);
      toast(t("common.error"));
    }
  }, [projectId, toast, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function confirmDelete() {
    if (!toDelete || busy) return;
    setBusy(true);
    try {
      await deleteVaultEntry(toDelete.id);
      setToDelete(null);
      await load();
      onChange?.();
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  const keyMissing = status !== null && !status.ready;

  const addBtn =
    canManage && !keyMissing ? (
      <Btn small tone="brand" onClick={() => setAdding(true)}>
        <Plus className="size-4" />
        {t("projects.addCredential")}
      </Btn>
    ) : null;

  return (
    <Card>
      <CardHead title={t("projects.vaultTitle")} sub={t("projects.vaultIntro")} actions={addBtn} />

      {keyMissing ? (
        <div className="px-6 pt-1">
          <div className="crm-alert">
            <ShieldAlert className="size-4 shrink-0" />
            <span>{t(status?.reason === "invalid" ? "vault.keyInvalid" : "vault.keyMissing")}</span>
          </div>
        </div>
      ) : null}

      {rows === null ? (
        // Wordless on purpose: the empty card below is a sentence, and the two
        // states must not be confusable at a glance.
        <div className="flex justify-center py-14 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title={t("projects.noVault")}
          body={t(keyMissing ? "vault.emptyNoKeyBody" : "projects.noVaultBody")}
          action={addBtn}
        />
      ) : (
        <>
          <div className="crm-list-head" style={{ ...listCard(COLS), gridTemplateColumns: COLS }}>
            <span>{t("vault.col.entry")}</span>
            <span>{t("vault.client")}</span>
            <span>{t("vault.host")}</span>
            <span>{t("vault.username")}</span>
            <span>{t("vault.col.secret")}</span>
            <span>{t("vault.col.updated")}</span>
            <span />
          </div>

          <div className="crm-rows pt-0" style={listCard(COLS)}>
            {rows.map((entry) => (
              <VaultRow
                key={entry.id}
                entry={entry}
                canManage={canManage}
                onReveal={setRevealing}
                onEdit={setEditing}
                onDelete={setToDelete}
              />
            ))}
          </div>

          {/* Only when something was left out. A footer that printed "6 of 6" on
              every project would be noise; a list that silently stopped at a
              hundred would be a lie. */}
          {total > rows.length ? (
            <div className="crm-tfoot">
              {t("vault.showing", { from: 1, to: rows.length, total })}
              <Link to="/vault" className="crm-btn crm-btn-sm crm-push">
                {t("projects.openVault")}
              </Link>
            </div>
          ) : null}
        </>
      )}

      {adding || editing ? (
        <VaultModal
          entry={editing}
          // The project and its client are filled in for a new entry, because
          // this tab is the answer to "whose credential is this" - asking again
          // in the dialog is asking a question the screen already answered, and
          // an entry saved without the project would vanish from the list it was
          // created in.
          preset={{ projectId, clientId }}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={() => {
            void load();
            onChange?.();
          }}
        />
      ) : null}

      {revealing ? <RevealDialog entry={revealing} onClose={() => setRevealing(null)} /> : null}

      {toDelete ? (
        <Modal
          title={t("vault.deleteTitle")}
          label={t("vault.deleteTitle")}
          onClose={() => setToDelete(null)}
          footer={
            <>
              <Btn onClick={() => setToDelete(null)} disabled={busy}>
                {t("common.cancel")}
              </Btn>
              <Btn tone="danger" onClick={() => void confirmDelete()} disabled={busy}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : null}
                {t("common.delete")}
              </Btn>
            </>
          }
        >
          {/* Naming the row, because the confirm opens from a menu that has
              already closed over it - and because this is the one delete on the
              project page whose contents cannot be reconstructed from anywhere
              else. */}
          <p className="crm-sub">
            {t("vault.deleteBody", { title: toDelete.title, client: toDelete.clientName ?? "-" })}
          </p>
        </Modal>
      ) : null}
    </Card>
  );
}
